import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import * as turf from '@turf/turf';
import { Feature, MultiPolygon, Polygon } from '@turf/turf';
import Button from '@mui/material/Button';

import MapCanvas from '../../MapObjects/MapCanvas/MapCanvas.jsx';
import MapPopup from '../../MapObjects/MapPopup';
import { mapToCanvas } from '../../MapObjects/MapCanvas/MapCanvasClickable';
import { useGoogleMap } from '../../../context/GoogleMapContext';
import useMapEvent from '../../DataLayer/hooks/useMapEvent';
import { isDemandLayerType } from '../../../model/surveys/country/SurveyCountryStrategy';
import {
  getNullValueRegions,
  hasSignificantNullZonesInField,
  isCellInsideField,
  isNullInterval,
  restoreNullValues,
} from '../nullValueRegions';
import type Field from '../../../model/Field';
import type { PrescriptionJob } from '../PrescriptionJob';
import type { BulkPrescriptionJobs } from '../SeedingJob/SeedingJobCommonTypes';

type FieldRegions = {
  field: Field;
  jobFieldId: PrescriptionJob['fieldId'];
  values: PrescriptionJob['values'];
  fieldSize: PrescriptionJob['fieldSize'];
  polygon: Feature<Polygon | MultiPolygon> | Polygon | MultiPolygon;
  regions: ReturnType<typeof getNullValueRegions>;
  hasSignificantNullZones: boolean;
};

type BulkNullValueCanvasProps = {
  jobs: BulkPrescriptionJobs;
  fields: Field[];
  onAdd: () => void;
  label: string;
  highlightFromButton?: boolean;
};

const BulkNullValueCanvas = ({
  jobs,
  fields,
  onAdd,
  label,
  highlightFromButton = false,
}: BulkNullValueCanvasProps) => {
  const map = useGoogleMap();
  const [popupPosition, setPopupPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [highlightAll, setHighlightAll] = useState(false);
  const fieldRegions = useRef(new Map<Field['fieldId'], FieldRegions>());
  const entries = useMemo(() => {
    const nextFieldRegions = new Map<Field['fieldId'], FieldRegions>();
    const nextEntries = fields.reduce<
      Array<{
        field: Field;
        job: PrescriptionJob;
        polygon: Feature<Polygon | MultiPolygon> | Polygon | MultiPolygon;
        regions: ReturnType<typeof getNullValueRegions>;
        canAdd: boolean;
      }>
    >((result, field) => {
      const job = jobs[field.fieldId];
      if (!job?.values?.[0]?.length || isDemandLayerType(job.layerType)) return result;
      let cached = fieldRegions.current.get(field.fieldId);
      if (
        !cached ||
        cached.field !== field ||
        cached.jobFieldId !== job.fieldId ||
        cached.values !== job.values ||
        cached.fieldSize !== job.fieldSize
      ) {
        const polygon = turf.polygon(field.polygon.coordinates);
        const regions = getNullValueRegions(restoreNullValues(job.values), (x, y) =>
          isCellInsideField(x, y, job.values, field.bounds, polygon)
        );
        cached = {
          field,
          jobFieldId: job.fieldId,
          values: job.values,
          fieldSize: job.fieldSize,
          polygon,
          regions,
          hasSignificantNullZones:
            regions.regions.length > 0 && hasSignificantNullZonesInField(job, field),
        };
      }
      nextFieldRegions.set(field.fieldId, cached);
      const { polygon, regions, hasSignificantNullZones } = cached;
      if (regions.regions.length > 0) {
        result.push({
          field,
          job,
          polygon,
          regions,
          canAdd: !job.intervals.some(isNullInterval) && hasSignificantNullZones,
        });
      }
      return result;
    }, []);
    fieldRegions.current = nextFieldRegions;
    return nextEntries;
  }, [jobs, fields]);

  const isOverNull = useCallback(
    (latLng: { lat: number; lng: number }): boolean => {
      const point = turf.point([latLng.lng, latLng.lat]);
      return entries.some(({ field, job, polygon, regions, canAdd }) => {
        if (!canAdd || !turf.booleanPointInPolygon(point, polygon)) return false;
        const { x, y } = mapToCanvas(
          latLng.lat,
          latLng.lng,
          job.values[0].length,
          job.values.length,
          field.bounds,
          true
        );
        const row = regions.regionIds[y];
        return Boolean(row && row[x] !== undefined && row[x] >= 0);
      });
    },
    [entries]
  );

  useMapEvent(map, 'mousemove', (event: { latLng: { lat: () => any; lng: () => any } }) => {
    setHighlightAll(isOverNull({ lat: event.latLng.lat(), lng: event.latLng.lng() }));
  });
  useMapEvent(map, 'mouseout', () => setHighlightAll(false));
  useMapEvent(map, 'click', (event: { latLng: { lat: () => any; lng: () => any } }) => {
    const latLng = { lat: event.latLng.lat(), lng: event.latLng.lng() };
    setPopupPosition(isOverNull(latLng) ? latLng : null);
  });

  return (
    <>
      {entries.map(({ field, job, regions }) => (
        <MapCanvas
          key={`bulk-null-${field.fieldId}`}
          zIndex={6}
          redrawOnCallbackChange={false}
          clipPathPolygon={field.polygon.coordinates[0]}
          bounds={field.bounds}
          width={job.values[0].length}
          height={job.values.length}
          updateDependencies={[highlightAll, highlightFromButton, regions]}
          onDraw={(ctx) => {
            ctx.clearRect(0, 0, job.values[0].length, job.values.length);
            if (!highlightAll && !highlightFromButton) return;
            ctx.fillStyle = 'rgba(35, 100, 240, 0.45)';
            regions.regions.forEach((region) =>
              region.forEach(({ x, y }) => ctx.fillRect(x, y, 1, 1))
            );
          }}
        />
      ))}
      {popupPosition && (
        <MapPopup
          shown
          selected
          clickable
          position={popupPosition}
          onClickAway={() => setPopupPosition(null)}
          onClick={() => {
            onAdd();
            setPopupPosition(null);
            setHighlightAll(false);
          }}
        >
          <Button>{label}</Button>
        </MapPopup>
      )}
    </>
  );
};

export default memo(BulkNullValueCanvas);
